import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  // -- Public area (PublicLayout) --
  {
    path: '/',
    name: 'landing',
    component: () => import('@/pages/public/LandingPage.vue'),
    meta: { requiresAuth: false, layout: 'public', title: 'public.landing.hero.title' },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/pages/public/AboutPage.vue'),
    meta: { requiresAuth: false, layout: 'public', title: 'public.about.hero.title' },
  },
  {
    path: '/features',
    name: 'features',
    component: () => import('@/pages/public/FeaturesPage.vue'),
    meta: { requiresAuth: false, layout: 'public', title: 'public.features.hero.title' },
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/pages/public/PricingPage.vue'),
    meta: { requiresAuth: false, layout: 'public', title: 'public.pricing.hero.title' },
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('@/pages/public/BlogPage.vue'),
    meta: { requiresAuth: false, layout: 'public', title: 'public.blog.title' },
  },
  {
    path: '/screener',
    name: 'screener',
    component: () => import('@/pages/public/ScreenerPage.vue'),
    meta: { requiresAuth: false, layout: 'public', title: 'screener.title' },
  },

  // -- Auth (guest only) --
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { requiresAuth: false, guest: true, layout: 'none', title: 'auth.login.title' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { requiresAuth: false, guest: true, layout: 'none', title: 'auth.register.title' },
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { requiresAuth: false, guest: true, layout: 'none', title: 'auth.forgot.title' },
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { requiresAuth: false, guest: true, layout: 'none', title: 'auth.reset.title' },
  },
  {
    path: '/verify-email',
    name: 'verifyEmail',
    component: () => import('@/pages/auth/VerifyEmailPage.vue'),
    meta: { requiresAuth: false, guest: true, layout: 'none', title: 'auth.verify.title' },
  },

  // -- Protected routes (AppLayout) --
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/pages/dashboard/DashboardPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'dashboard.title' },
  },
  {
    path: '/bots',
    name: 'bots',
    component: () => import('@/pages/bots/BotsPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'bots.title' },
  },
  {
    path: '/bots/create',
    name: 'createBot',
    component: () => import('@/pages/bots/CreateBotPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'bots.createPage.title' },
  },
  {
    path: '/settings/api-keys',
    name: 'apiKeys',
    component: () => import('@/pages/settings/ApiKeysPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'apiKeys.title' },
  },
  {
    path: '/settings/security',
    name: 'security',
    component: () => import('@/pages/settings/TotpSettingsPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'settings.totp.title' },
  },

  // -- 404 --
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { requiresAuth: false, title: '404' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

// -- Navigation Guards --
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  // Wait for auth store initialization (first load)
  if (authStore.isLoading) {
    await authStore.init()
  }

  // Guest routes: if authenticated then redirect to dashboard
  if (to.meta.guest && authStore.isAuthenticated) {
    return { path: '/dashboard' }
  }

  // Protected routes: if unauthenticated then redirect to login
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
})

export default router
